/**
 * ---------------------------------------------------------------------------------------------------------------------
 * LEGAL GUARANTEE
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Opens the shared EU legal guarantee notice dialog.
 *
 * Everything is delegated from `document`, so triggers keep working after the cart drawer re-renders through the
 * Section Rendering API, after quick view injects a product into a drawer, and after the theme editor swaps a section
 * or block, with no re-initialisation.
 *
 * A trigger is either an element carrying `data-legal-guarantee-open`, or any same-origin link pointing at the
 * fallback page. That second rule is what makes footer menu links, announcement bars and rich text links open the
 * dialog too, without the merchant having to do anything special.
 *
 * Progressive enhancement: every trigger is a real link to a real page. Without JavaScript, and on modified clicks,
 * the shopper simply lands on that page, which renders the same notice.
 */

(() => {
  if (window.legalGuaranteeReady) {
    return;
  }

  window.legalGuaranteeReady = true;

  const DIALOG_ID = 'legal-guarantee-dialog';

  let returnFocusTo = null;
  let pausedHost = null;
  let swallowNextEscapeKeyup = false;
  let warmed = false;

  const getDialog = () => document.getElementById(DIALOG_ID);

  const findTrigger = (target, dialog) => {
    if (!(target instanceof Element)) {
      return null;
    }

    const element = target.closest('[data-legal-guarantee-open], a[href]');

    if (!element) {
      return null;
    }

    if (element.hasAttribute('data-legal-guarantee-open')) {
      return element;
    }

    const handle = dialog.dataset.pageHandle;

    if (!handle || !element.href) {
      return null;
    }

    let url;

    try {
      url = new URL(element.href, location.href);
    } catch (error) {
      return null;
    }

    const path = url.pathname.replace(/\/+$/, '');

    return url.origin === location.origin && path.endsWith('/pages/' + handle) ? element : null;
  };

  /* ===================================================================================================================
   * THEME ADAPTER — the only part of this file that changes per theme.
   * ===================================================================================================================
   *
   * If the theme traps focus inside its drawers, that trap fights a top-layer <dialog>: its Tab handler can cycle
   * focus back into the drawer underneath us, and some implementations also drag focus back on `focusin`. So the
   * trap is released while the notice is open and restored when it closes.
   *
   * Answer audit question 11 first, then fill in the two functions below. Everything else in this file is portable.
   *
   * HOST_SELECTOR matches the open drawer/modal a trigger can live inside. Add the theme's own drawer classes to it.
   *
   * Two shapes seen so far:
   *
   *   a) Per-element trap (focus-trap library, a `focusTrap` getter on the drawer element):
   *        release: host.focusTrap?.pause()
   *        restore: host.focusTrap?.unpause()
   *
   *   b) One global trap shared by every drawer (a single pair of document listeners):
   *        release: window.theme?.a11y?.removeTrapFocus()
   *        restore: window.theme?.a11y?.trapFocus(host, host.querySelector('button') || host)
   *
   * If the theme has no focus trap at all, leave both as no-ops — the rest still works.
   *
   * Both calls are wrapped: the theme's script is usually deferred and this file is not, so on a very early click
   * its namespace may not exist yet, and the drawer may be torn down while the notice is open.
   */
  /* Focal 12.5.0 (Maestrooo) — shape (a), a per-element focus-trap handle.
   *
   * Focal's overlays are custom elements that all extend OpenableElement, which exposes a public `focusTrap`
   * getter wrapping the focus-trap library. So one pair of calls covers the cart drawer, the quick buy drawer
   * and popover, the predictive search drawer, and every modal and popover.
   *
   * The selector cannot use the kit's default `[role="dialog"]` / `[aria-modal="true"]`: Focal sets neither.
   * Its overlays are keyed on a bare `open` attribute, so the element names are the only reliable handle. If a
   * future Focal release adds another overlay element, add its tag here.
   *
   * Why pausing matters more here than the Tab cycling the kit describes: Focal configures the trap with
   * `clickOutsideDeactivates` and `onDeactivate: () => this.open = false`. Our backdrop and close button are
   * outside the drawer, so without pausing, one click on either would deactivate the trap and close the cart
   * drawer underneath the notice. Pausing also removes the library's document Escape listener, so a single
   * Escape closes the notice only — which is why this theme needs no separate Escape guard.
   */
  const HOST_SELECTOR = [
    'cart-drawer[open]',
    'quick-buy-drawer[open]',
    'quick-buy-popover[open]',
    'predictive-search-drawer[open]',
    'drawer-content[open]',
    'modal-content[open]',
    'popover-content[open]'
  ].join(', ');

  const releaseHostTrap = (host) => {
    host?.focusTrap?.pause();
  };

  const restoreHostTrap = (host) => {
    host?.focusTrap?.unpause();
  };

  /* ================================================================================================================ */

  const pauseHostTrap = (trigger) => {
    const host = trigger.closest(HOST_SELECTOR);

    try {
      releaseHostTrap(host);
    } catch (error) {
      // No trap was active, nothing to release.
    }

    return host || null;
  };

  const resumeHostTrap = () => {
    if (pausedHost && pausedHost.isConnected && pausedHost.hasAttribute('open')) {
      try {
        restoreHostTrap(pausedHost);
      } catch (error) {
        // The drawer was torn down while the notice was open, nothing to restore.
      }
    }

    pausedHost = null;
  };

  /**
   * Idempotent, so it is safe to call from both the `close` event and directly from the paths we close ourselves.
   * Leaving a host drawer without focus management would be worse than the original problem, so this must not
   * depend on a single event firing.
   */
  const handleClosed = () => {
    resumeHostTrap();

    if (returnFocusTo && returnFocusTo.isConnected) {
      returnFocusTo.focus({ preventScroll: true });
    }

    returnFocusTo = null;
  };

  // Hovering or focusing a trigger warms the image cache so the dialog opens without a blank flash. It never opens
  // the dialog: touch devices have no hover, and a modal appearing on hover is jarring.
  const warm = (event) => {
    if (warmed) {
      return;
    }

    const dialog = getDialog();

    if (!dialog || dialog.open || !findTrigger(event.target, dialog)) {
      return;
    }

    const image = dialog.querySelector('.legal-guarantee-notice__image');

    if (image) {
      new Image().src = image.currentSrc || image.src;
      warmed = true;
    }
  };

  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);

  // Listening in the CAPTURE phase is deliberate, and the storefront does not work reliably without it.
  //
  // Link hijackers are common and come in two shapes. Prefetch/instant-navigation scripts register a capture-phase
  // click handler on the link itself, call preventDefault() and re-dispatch a synthetic click; installed apps
  // register a document-level bubble handler and navigate themselves. Either would win over a bubble listener here,
  // our `defaultPrevented` guard would politely stand down, and the trigger would simply navigate to the fallback
  // page instead of opening the dialog.
  //
  // Capture at document runs before every element-level and bubble listener, so we see the click first.
  // stopPropagation() then keeps the click away from the hijacker entirely, rather than relying on it to honour
  // defaultPrevented.
  //
  // Modified clicks are still checked here, so Cmd/Ctrl/Shift-click and middle-click keep opening the fallback page
  // in a new tab exactly as a normal link would.
  document.addEventListener(
    'click',
    (event) => {
      const dialog = getDialog();

      if (!dialog) {
        return;
      }

      if (dialog.open) {
        if (event.target === dialog || event.target.closest('[data-legal-guarantee-close]')) {
          dialog.close();
          handleClosed();
        }

        return;
      }

      // Let modified clicks fall through so the fallback page still opens in a new tab or window.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const trigger = findTrigger(event.target, dialog);

      if (!trigger) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      returnFocusTo = trigger;
      pausedHost = pauseHostTrap(trigger);

      dialog.showModal();
    },
    true
  );

  /**
   * Escape has to be contained, or it closes the drawer underneath us as well.
   *
   * Themes routinely close drawers with a document-level Escape handler, registered once per modal instance —
   * `document.addEventListener('keyup', e => e.code === 'Escape' && this.hide())` or the `keydown` equivalent. A
   * native <dialog> closes on the Escape *keydown*, so without this guard one press would close the notice AND the
   * cart drawer, quick view or modal hosting it. This is worth keeping even when the theme looks innocent: app
   * embeds do it too.
   *
   * stopPropagation() in the capture phase keeps the event away from those document-level listeners. It does not
   * cancel the default action, so the dialog still closes exactly as the platform intends. The matching keyup is
   * swallowed too, because it arrives after the dialog has already closed and would otherwise reach the drawer.
   */
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') {
        return;
      }

      const dialog = getDialog();

      if (!dialog || !dialog.open) {
        return;
      }

      event.stopPropagation();
      swallowNextEscapeKeyup = true;
    },
    true
  );

  document.addEventListener(
    'keyup',
    (event) => {
      if (!swallowNextEscapeKeyup) {
        return;
      }

      if (event.key !== 'Escape' && event.code !== 'Escape') {
        return;
      }

      swallowNextEscapeKeyup = false;
      event.stopPropagation();
    },
    true
  );

  // "close" does not bubble, so it has to be caught in the capture phase. This is the catch-all: it covers Escape
  // and any close() call made by something other than the handler above.
  document.addEventListener(
    'close',
    (event) => {
      if (event.target.id === DIALOG_ID) {
        handleClosed();
      }
    },
    true
  );
})();
